import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { PERMISSION_GROUPS } from '@shripos/shared/constants';
import { Alert, Dialog, PinPad, Spinner } from '@shripos/ui';
import { api } from '../lib/api.js';
import { useOverrideStore } from '../lib/override.js';
import { usePrintStore } from '../lib/print.js';

const permissionLabel = Object.fromEntries(
  PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => [p.key, p.label])),
);

/** Renders print jobs into #print-root (see index.css @media print). */
export function PrintHost() {
  const job = usePrintStore((s) => s.job);
  if (!job) return null;
  return createPortal(
    <div id="print-root" data-width={job.width}>
      <style>{`@page { size: ${job.width} auto; margin: 0; }`}</style>
      {job.node}
    </div>,
    document.body,
  );
}

/** Manager approval: pick an approver, enter their PIN, hand the single-use token back. */
export function OverrideHost() {
  const request = useOverrideStore((s) => s.request);
  const close = useOverrideStore((s) => s.close);
  const [approver, setApprover] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const approvers = useQuery({
    queryKey: ['approvers', request?.permission],
    queryFn: () => api.get(`/auth/approvers?permission=${request.permission}`),
    enabled: Boolean(request),
  });

  if (!request) return null;
  const done = (token) => {
    setApprover(null);
    setError(null);
    close(token);
  };
  const submit = async (pin) => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/auth/override', { userId: approver.id, pin, permission: request.permission });
      done(res.overrideToken);
    } catch (err) {
      setError(err.message);
      setAttempt((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => done(null)} title="Manager approval needed" size="sm">
      <div className="space-y-4">
        <Alert tone="warning">
          <span className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 size-4 shrink-0" />
            <span>
              {request.message}
              <span className="mt-1 block text-xs opacity-80">
                Permission: {permissionLabel[request.permission] ?? request.permission}
              </span>
            </span>
          </span>
        </Alert>
        {!approver ? (
          approvers.isLoading ? (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          ) : approvers.data?.items.length ? (
            <div className="grid grid-cols-2 gap-2">
              {approvers.data.items.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setApprover(a)}
                  className="rounded-xl p-3 text-left ring-1 ring-slate-200 hover:bg-brand-50 hover:ring-brand-300"
                >
                  <span className="block font-medium">{a.name}</span>
                  <span className="text-xs text-slate-500">{a.roleName}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-600">
              Nobody with a PIN can approve this here. Set a manager PIN in Staff.
            </p>
          )
        ) : (
          <div>
            <p className="mb-3 text-center text-sm">
              <button className="text-brand-700 hover:underline" onClick={() => setApprover(null)}>
                ← choose someone else
              </button>
              <span className="mt-1 block font-semibold">{approver.name}, enter your PIN</span>
            </p>
            <PinPad key={attempt} onSubmit={submit} disabled={busy} error={error} />
          </div>
        )}
      </div>
    </Dialog>
  );
}
